// src/pages/problem-bank/MyAssignments.tsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { supabase } from '@/lib/supabaseClient';
import {
  ArrowLeft,
  Search,
  Eye,
  Clock,
  Building2,
  MapPin,
  Calendar,
  Target,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  TrendingUp,
  Users,
  FileText,
  DollarSign,
  ChevronRight,
  UserCheck,
  BarChart3,
  Shield,
  Inbox,
  Wifi,
  WifiOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

const STATUS_COLORS: Record<string, string> = {
  lead: 'bg-gray-100 text-gray-700',
  prospect: 'bg-blue-100 text-blue-700',
  verified: 'bg-green-100 text-green-700',
  assigned: 'bg-purple-100 text-purple-700',
  solved: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-yellow-100 text-yellow-700',
  under_review: 'bg-blue-100 text-blue-700',
  accepted: 'bg-green-100 text-green-700',
  converted: 'bg-gray-100 text-gray-700',
  rejected: 'bg-red-100 text-red-700'
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-green-100 text-green-700'
};

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

export default function MyAssignments() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isSupabaseConnected } = useData();
  const { toast } = useToast();
  
  // Determine user role type
  const isProblemBankAnalyst = user?.analyst_type === 'problem_bank' || user?.role === 'senior_analyst';
  const isSeniorAnalyst = user?.role === 'senior_analyst';
  const isBusinessAnalyst = user?.analyst_type === 'business';
  
  const [problemAssignments, setProblemAssignments] = useState<any[]>([]);
  const [submissionAssignments, setSubmissionAssignments] = useState<any[]>([]);
  const [allAssignments, setAllAssignments] = useState<any[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState('active');

  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const loadAssignmentsRef = useRef<() => Promise<void>>();

  const loadAssignments = useCallback(async () => {
    if (!user?.id) return;
    
    setIsRefreshing(true);
    try {
      console.log('📊 Loading assignments for analyst:', user.id);
      
      // 1. Fetch problems assigned from problem_bank table
      const { data: problemsData, error: problemsError } = await supabase
        .from('problem_bank')
        .select('*')
        .eq('assigned_analyst_id', user.id)
        .order('updated_at', { ascending: false });

      if (problemsError) throw problemsError;

      console.log('📊 Problem assignments found:', problemsData?.length || 0);
      
      // Parse problem JSON fields
      const parsedProblems = (problemsData || []).map(problem => ({
        ...problem,
        type: 'problem',
        assignmentType: 'problem_bank',
        anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
        quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
        assignment: typeof problem.assignment === 'string' ? JSON.parse(problem.assignment) : problem.assignment,
        priorityScore: problem.priority_score,
        problemId: problem.problem_id,
        title: problem.title,
        description: problem.description,
        status: problem.status,
        submittedAt: problem.created_at,
        targetDate: problem.assignment?.targetDeliveryDate,
        progress: problem.assignment?.milestones ? 
          Math.round((problem.assignment.milestones.filter((m: any) => m.completed).length / problem.assignment.milestones.length) * 100) : 0
      }));

      setProblemAssignments(parsedProblems);

      // 2. Fetch submissions assigned from external_submissions table
      const { data: submissionsData, error: submissionsError } = await supabase
        .from('external_submissions')
        .select('*')
        .eq('assigned_analyst_id', user.id)
        .order('submitted_at', { ascending: false });

      if (submissionsError) throw submissionsError;

      console.log('📊 Submission assignments found:', submissionsData?.length || 0);

      // Parse submission fields
      const parsedSubmissions = (submissionsData || []).map(sub => ({
        ...sub,
        type: 'submission',
        assignmentType: 'external_submission',
        problemId: sub.id,
        title: sub.problem_title,
        description: sub.problem_description,
        status: sub.status,
        submittedAt: sub.submitted_at,
        targetDate: null,
        progress: sub.status === 'converted' ? 100 : sub.status === 'under_review' ? 50 : 0,
        anchor: {
          name: sub.company_name || sub.contact_name,
          location: sub.location || 'Not specified'
        },
        quantification: {
          totalLossPerYear: sub.total_annual_cost || 0
        },
        priorityScore: {
          priorityTier: sub.urgency === 'high' ? 'high' : sub.urgency === 'medium' ? 'medium' : 'low'
        }
      }));

      setSubmissionAssignments(parsedSubmissions);

      // Combine both types of assignments
      const combined = [...parsedProblems, ...parsedSubmissions];
      setAllAssignments(combined);
      setFilteredAssignments(combined);
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load assignments',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [user?.id, toast]);

  // Store loadAssignments in ref for realtime callbacks
  useEffect(() => {
    loadAssignmentsRef.current = loadAssignments;
  }, [loadAssignments]);

  // Load assignments when component mounts
  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // Set up polling (every 15 seconds)
  useEffect(() => {
    console.log('🚀 Setting up polling for assignments (every 15 seconds)...');
    pollingInterval.current = setInterval(() => {
      if (loadAssignmentsRef.current) {
        console.log('🔄 Polling: Refreshing assignments...');
        loadAssignmentsRef.current();
      }
    }, 15000);

    return () => {
      console.log('🛑 Cleaning up polling interval');
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, []);

  // Set up WebSocket realtime subscriptions
  useEffect(() => {
    console.log('🔌 Setting up WebSocket realtime subscription for problem_bank...');
    
    const problemChannel = supabase
      .channel('my-assignments-problems')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'problem_bank' },
        (payload) => {
          console.log('📡 problem_bank realtime event:', payload.eventType);
          if (loadAssignmentsRef.current) {
            loadAssignmentsRef.current();
          }
        }
      )
      .subscribe((status) => {
        console.log('🔌 problem_bank subscription status:', status);
      });

    const submissionChannel = supabase
      .channel('my-assignments-submissions')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'external_submissions' },
        (payload) => {
          console.log('📡 external_submissions realtime event:', payload.eventType);
          if (loadAssignmentsRef.current) {
            loadAssignmentsRef.current();
          }
        }
      )
      .subscribe((status) => {
        console.log('🔌 external_submissions subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up WebSocket subscriptions');
      supabase.removeChannel(problemChannel);
      supabase.removeChannel(submissionChannel);
    };
  }, []);

  // Refresh on tab focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ Tab focused - refreshing assignments');
        if (loadAssignmentsRef.current) {
          loadAssignmentsRef.current();
        }
      }
    };
    
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => window.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    filterAssignments();
  }, [allAssignments, searchQuery, statusFilter, typeFilter, activeTab]);

  const filterAssignments = () => {
    let filtered = [...allAssignments];

    // Apply tab filter
    if (activeTab === 'active') {
      filtered = filtered.filter(a => 
        a.status === 'assigned' || a.status === 'pending' || a.status === 'under_review'
      );
    } else if (activeTab === 'completed') {
      filtered = filtered.filter(a => 
        a.status === 'solved' || a.status === 'converted' || a.status === 'accepted'
      );
    }

    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.title?.toLowerCase().includes(query) ||
        a.problemId?.toLowerCase().includes(query) ||
        a.anchor?.name?.toLowerCase().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(a => a.type === typeFilter);
    }

    setFilteredAssignments(filtered);
  };

  const getDaysRemaining = (assignment: any) => {
    if (!assignment.targetDate) return null;
    const target = new Date(assignment.targetDate);
    const now = new Date();
    const diffTime = target.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleViewAssignment = (assignment: any) => {
    if (assignment.type === 'problem') {
      navigate(`/problem-bank/problem/${assignment.id}`);
    } else {
      navigate(`/problem-bank/submissions/review/${assignment.id}`, {
        state: { submission: assignment }
      });
    }
  };

  const handleTrackProgress = (assignment: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (assignment.type === 'problem') {
      navigate(`/problem-bank/problem/${assignment.id}/progress`);
    } else {
      // For submissions, just open the review page
      navigate(`/problem-bank/submissions/review/${assignment.id}`, {
        state: { submission: assignment }
      });
    }
  };

  const handleRefresh = async () => {
    await loadAssignments();
    toast({ title: "Refreshed", description: "Assignments updated." });
  };

  const stats = {
    active: allAssignments.filter(a => a.status === 'assigned' || a.status === 'pending' || a.status === 'under_review').length,
    completed: allAssignments.filter(a => a.status === 'solved' || a.status === 'converted' || a.status === 'accepted').length,
    critical: allAssignments.filter(a => a.priorityScore?.priorityTier === 'critical' || a.priorityScore?.priorityTier === 'high').length,
    submissions: submissionAssignments.length,
    problems: problemAssignments.length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/problem-bank')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">My Assignments</h1>
          <p className="text-muted-foreground mt-1">
            Problems and submissions assigned to you - track progress and milestones
          </p>
          <div className="flex items-center gap-2 mt-2">
            <p className="text-xs text-muted-foreground">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
            <div className="flex items-center gap-1">
              {isSupabaseConnected ? (
                <>
                  <Wifi className="h-3 w-3 text-green-500" />
                  <span className="text-xs text-green-600">Live</span>
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3 text-red-500" />
                  <span className="text-xs text-red-600">Reconnecting...</span>
                </>
              )}
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
          {isRefreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Active Assignments</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{stats.completed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">High Priority</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.critical}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Problems</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.problems}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Submissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-cyan-600">{stats.submissions}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="active">Active Assignments</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by title, company, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="problem">Problems</SelectItem>
                <SelectItem value="submission">Submissions</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="solved">Solved</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Assignments Grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-6 w-3/4 mb-4" />
                    <Skeleton className="h-4 w-1/2 mb-2" />
                    <Skeleton className="h-4 w-2/3 mb-4" />
                    <Skeleton className="h-2 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredAssignments.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No assignments found</h3>
                <p className="text-muted-foreground text-center max-w-md">
                  {activeTab === 'active' 
                    ? "You don't have any active assignments. When problems or submissions are assigned to you, they will appear here."
                    : "No completed assignments yet. Track your progress here."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredAssignments.map((assignment) => {
                const daysRemaining = getDaysRemaining(assignment);
                const isOverdue = daysRemaining !== null && daysRemaining < 0;
                const isSubmission = assignment.type === 'submission';
                
                return (
                  <Card 
                    key={`${assignment.type}-${assignment.id}`}
                    className={`hover:shadow-lg transition-shadow cursor-pointer ${isSubmission ? 'border-l-4 border-l-cyan-500' : ''}`}
                    onClick={() => handleViewAssignment(assignment)}
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex gap-2 flex-wrap">
                          {isSubmission && (
                            <Badge className="bg-cyan-100 text-cyan-700">
                              <Inbox className="h-3 w-3 mr-1" />
                              Submission
                            </Badge>
                          )}
                          <Badge variant="outline" className={STATUS_COLORS[assignment.status]}>
                            {assignment.status?.toUpperCase()}
                          </Badge>
                          {assignment.priorityScore?.priorityTier && (
                            <Badge variant="outline" className={PRIORITY_COLORS[assignment.priorityScore.priorityTier]}>
                              {assignment.priorityScore.priorityTier.toUpperCase()}
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs font-mono text-muted-foreground">
                          {assignment.problemId?.slice(0, 8)}...
                        </span>
                      </div>

                      <h3 className="font-semibold text-lg mb-2 line-clamp-2">{assignment.title}</h3>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span className="truncate">{assignment.anchor?.name || 'Unknown'}</span>
                        </div>
                        {assignment.anchor?.location && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span>{assignment.anchor.location}</span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-medium">{assignment.progress}%</span>
                        </div>
                        <Progress value={assignment.progress} className="h-2" />
                        
                        {assignment.targetDate && (
                          <div className="flex justify-between items-center text-sm">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Target className="h-4 w-4" />
                              <span>Target: {new Date(assignment.targetDate).toLocaleDateString()}</span>
                            </div>
                            <Badge variant="outline" className={
                              isOverdue ? 'bg-red-100 text-red-700' :
                              daysRemaining > 14 ? 'bg-green-100 text-green-700' :
                              daysRemaining > 7 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-orange-100 text-orange-700'
                            }>
                              {isOverdue ? `${Math.abs(daysRemaining)} days overdue` : daysRemaining > 0 ? `${daysRemaining} days left` : 'Due today'}
                            </Badge>
                          </div>
                        )}

                        {/* Milestones - only for problems */}
                        {!isSubmission && assignment.assignment?.milestones && assignment.assignment.milestones.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-sm font-medium mb-2">Milestones</p>
                            {assignment.assignment.milestones.slice(0, 3).map((milestone: any, i: number) => (
                              <div key={i} className="flex items-center justify-between text-xs mb-1">
                                <span className="text-muted-foreground truncate flex-1">{milestone.description}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">
                                    {new Date(milestone.targetDate).toLocaleDateString()}
                                  </span>
                                  {milestone.completed ? (
                                    <CheckCircle className="h-3 w-3 text-green-600 ml-2 flex-shrink-0" />
                                  ) : (
                                    <Clock className="h-3 w-3 text-yellow-600 ml-2 flex-shrink-0" />
                                  )}
                                </div>
                              </div>
                            ))}
                            {assignment.assignment.milestones.length > 3 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                +{assignment.assignment.milestones.length - 3} more milestones
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t flex justify-between items-center text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <DollarSign className="h-4 w-4" />
                          <span>{formatCurrency(assignment.quantification?.totalLossPerYear || 0)}/yr</span>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="gap-1"
                            onClick={(e) => handleTrackProgress(assignment, e)}
                          >
                            <BarChart3 className="h-4 w-4" />
                            {isSubmission ? 'Review' : 'Track Progress'}
                          </Button>
                          <Button variant="ghost" size="sm" className="gap-1">
                            <Eye className="h-4 w-4" />
                            View Details
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}